import * as React from 'react';
import {
  Clock3,
  Cpu,
  FileClock,
  UserCheck,
  XCircle,
} from 'lucide-react';
import type {
  DeliveryVoucherIndicatorsResponse,
  DeliveryVoucherIndicatorType,
} from 'contracts/DeliveryVouchers';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { cn } from 'lib/utils';

type Props = {
  indicators?: DeliveryVoucherIndicatorsResponse;
  activeIndicator?: DeliveryVoucherIndicatorType;
  loading?: boolean;
  onToggleIndicator: (indicator?: DeliveryVoucherIndicatorType) => void;
};

type IndicatorCardProps = {
  title: string;
  quantity?: number;
  percentage?: number;
  icon: React.ReactNode;
  active?: boolean;
  loading?: boolean;
  onClick: () => void;
};

const IndicatorCard = ({
  title,
  quantity,
  percentage,
  icon,
  active,
  loading,
  onClick,
}: IndicatorCardProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className={cn(
      'block h-full w-full text-left transition disabled:cursor-wait',
      loading && 'opacity-30'
    )}
  >
    <Card
      className={cn(
        'h-full min-h-[126px] min-w-0 border-2 border-transparent transition',
        active && 'border-orange-500'
      )}
    >
      <CardHeader className="p-4 pb-3">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <h2 className="min-w-0 font-GilroySemibold text-sm leading-tight text-slate-800">
            {title}
          </h2>
          <div className="shrink-0 text-slate-800">{icon}</div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex items-end justify-between gap-3 font-GilroyBold text-orange-500">
          <span className="text-3xl leading-none">{quantity ?? 0}</span>
          <span className="text-2xl leading-none">
            {Number(percentage ?? 0).toFixed(2)}%
          </span>
        </div>
      </CardContent>
    </Card>
  </button>
);

// Configuração fixa dos cinco indicadores exibidos na tela.
const INDICATOR_CARDS: Array<{
  status: DeliveryVoucherIndicatorType;
  title: string;
  icon: React.ReactNode;
}> = [
  {
    status: 'approvedManual',
    title: 'Aprovados (Manual)',
    icon: <UserCheck className="h-5 w-5" />,
  },
  {
    status: 'approvedAutomatic',
    title: 'Aprovados (Automático)',
    icon: <Cpu className="h-5 w-5" />,
  },
  {
    status: 'waitingApproval',
    title: 'Aguardando aprovação',
    icon: <Clock3 className="h-5 w-5" />,
  },
  {
    status: 'waitingVoucher',
    title: 'Aguardando comprovante',
    icon: <FileClock className="h-5 w-5" />,
  },
  {
    status: 'voucherRejected',
    title: 'Reprovados',
    icon: <XCircle className="h-5 w-5" />,
  },
];

export const DeliveryVoucherIndicators = ({
  indicators,
  activeIndicator,
  loading,
  onToggleIndicator,
}: Props) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5 xl:gap-5">
    {INDICATOR_CARDS.map(({ status, title, icon }) => {
      const indicator = indicators?.[status];
      const active = activeIndicator === status;

      return (
        <IndicatorCard
          key={status}
          title={title}
          quantity={indicator?.qtd}
          percentage={indicator?.percent}
          icon={icon}
          active={active}
          loading={loading}
          // Clicar no card ativo remove o filtro; clicar em outro card aplica o status.
          onClick={() => onToggleIndicator(active ? undefined : status)}
        />
      );
    })}
  </div>
);
